import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";
import { trackClickedButton } from "utils/analytics";


const RESOURCES = [
  {
    label: "Quick Start Guide",
    type: "PDF, 242KB",
    url: "/resources/OKO Quick Start Guide.pdf",
  },
  {
    label: "How OKO Moderation Works",
    type: "Google Doc",
    url: "https://docs.google.com/document/d/189EZZU14Zh82qdijfawnOc_vl8rZAQPG87cgiHQen_Q/edit?usp=sharing",
  },
  {
    label: "Collaboration Rubric",
    type: "Google Doc",
    url: "https://docs.google.com/document/d/1eR3DQs4NWtr1I4wJ6UET3BdMIKTwYiaAzXnsE6Mqf2Y/edit?usp=sharing",
  },
  {
    label: "Math Learning Rubric",
    type: "Google Doc",
    url: "https://docs.google.com/document/d/1Ft503fy1Ui5ZDbZSDu8IsxxXqmVSSkjtrf_Ft4znzt4/edit?usp=sharing",
  },
  {
    label: "Math Talk Agreements",
    type: "PDF, 884KB",
    url: "/resources/Math Talk Agreements.pdf",
  },
  {
    label: "Sentence Starters",
    type: "PDF, 789KB",
    url: "/resources/Sentence Starters.pdf",
  },
  {
    label: "Meaningful Notes Template",
    type: "Google Doc",
    url: "https://docs.google.com/document/d/1yu5UuclqlMujEbSrCh-PQ2hEohAgngpOrSc_-1GoXvk/edit?usp=sharing",
  },
];

// "PDF, 242KB" -> "PDF": the size is display text, not a kind of resource.
const resourceTypeOf = (resource) => resource.type.split(",")[0].trim();

const trackOpenResource = (resource) =>
  trackClickedButton("Open Resource", {
    page_name: "Resource Hub",
    resource: resource.label,
    resource_type: resourceTypeOf(resource),
  });

export const ResourceHub = () => {
  return (
    <Container>
      <ContentHeader>
        Equip your classroom for successful group work! Use these resources to
        build a culture of accountable talk and help students master
        mathematical discourse.
      </ContentHeader>
      <Body>
        <ResourceCard>
          <ResourceList>
            {RESOURCES.map((resource) => (
              <ResourceItem
                key={resource.label}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOpenResource(resource)}
              >
                <Bullet>&bull;</Bullet>
                <ExternalLinkIcon />
                <ResourceText>
                  <ResourceLabel>{resource.label}</ResourceLabel>
                  {` (${resource.type})`}
                </ResourceText>
              </ResourceItem>
            ))}
          </ResourceList>
        </ResourceCard>
        <FeedbackCTA reportLocation="resource_hub" />
      </Body>
    </Container>
  );
};

const Container = styled.div`
  min-height: calc(100vh - 92px);
  background-color: ${colors.milkGlass200};
`;

const ContentHeader = styled.p`
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;
  padding: 32px 32px 0 32px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 32px;
`;

const ResourceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0px 1px 3px 0px rgba(14, 26, 49, 0.1),
    0px 1px 2px 0px rgba(14, 26, 49, 0.06);
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ResourceItem = styled.a`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  text-decoration: none;
  color: ${colors.nightlyWoods500};

  &:hover {
    color: ${colors.nightlyWoods500};
  }

  svg {
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const Bullet = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
`;

const ResourceText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.25px;
`;

const ResourceLabel = styled.span`
  text-decoration: underline;
`;

const ExternalLinkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21 9L21 3M21 3H15M21 3L13 11M10 5H7.8C6.11984 5 5.27976 5 4.63803 5.32698C4.07354 5.6146 3.6146 6.07354 3.32698 6.63803C3 7.27976 3 8.11984 3 9.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H14.2C15.8802 21 16.7202 21 17.362 20.673C17.9265 20.3854 18.3854 19.9265 18.673 19.362C19 18.7202 19 17.8802 19 16.2V14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
